import { MouseEventHandler, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Tooltip, Icon, EmptyState } from '@ets/design-system';

import dayjs from 'dayjs';

import { useDispatch, useSelector } from '_common/hooks';

import { setSelected } from '_common/components/Table/TableSlice';
import { getScopusCitationAuthors } from 'Editor/redux/CitationsSlice';

import {
  Table,
  Cell,
  type TableColumn,
  type TableRow,
  type onRowClickHandler,
} from '_common/components/Table';

import { ENTRIES } from '../CitationInformation/CitationInformation';
import { getSanitizedEntityName } from '../utils';

import styles from './CitationsList.module.scss';

type CitationsListProps = {
  citations: {
    list: ObjectId[];
    dict: Record<ObjectId, Citation>;
  };
  loading?: boolean;
  insertedCitations?: ObjectId[];
  libraryExportMode?: boolean;
  hasFilters?: boolean;
  // Handle click on import
  handleOnImportClick?: MouseEventHandler<HTMLButtonElement>;
};

const CitationsList = ({
  citations,
  insertedCitations,
  loading,
  libraryExportMode,
  hasFilters,
  handleOnImportClick,
}: CitationsListProps) => {
  const intl = useIntl();

  const selectedTab = useSelector((state) => state.tabMenu.citationsManager);
  const documentId = useSelector((state) => state.editor.status.documentId);
  const selected = useSelector((state) => state.table.selected);
  const [rowSelected, setRowSelected] = useState<number | undefined>();
  const dispatch = useDispatch();

  const getAuthor = (citation: Citation | undefined) => {
    let author = '';

    if (citation?.entry === 'data_file') {
      return author;
    }

    if (citation?.author && entryHasField(citation, 'author')) {
      if (Array.isArray(citation.author)) {
        author = citation.author
          .map((author) => getSanitizedEntityName(author))
          .filter((authorName) => authorName)
          .join(', ');
      } else {
        author = getSanitizedEntityName(citation.author) || citation.author;
      }
    } else {
      author = intl.formatMessage({ id: 'editor.templatePreview.properties.authors.noAuthors' });
    }

    //@ts-expect-error CitationSchema issue: missing 'scopus_id'
    if (citation?.scopus_id) {
      author += ' (…)';
    }

    return author;
  };

  const renderEmptyView = () => {
    if (selectedTab === 'importReferences') {
      return (
        <div className={styles.emptyView}>
          <EmptyState
            size="l"
            title={intl.formatMessage({
              id: 'editor.modals.citations.noReferencesFound',
            })}
            cta={{
              onClick: handleOnImportClick,
              children: intl.formatMessage({
                id: 'editor.modals.citations.importFromFile',
              }),
            }}
            testId="no-references-found"
            description={intl.formatMessage({
              id: 'NO_REFERENCES_FOUND_MESSAGE',
            })}
          />
        </div>
      );
    } else if (hasFilters) {
      return (
        <div className={styles.emptyView}>
          <EmptyState
            size="l"
            icon="magnifying-glass"
            title={intl.formatMessage({
              id: 'NO_RESULTS_FOUND',
            })}
            testId="no-results-found"
            description={intl.formatMessage({
              id: 'NO_RESULTS_FOUND_MESSAGE',
            })}
          />
        </div>
      );
    }

    return (
      <div className={styles.emptyView}>
        <EmptyState
          size="l"
          title={intl.formatMessage({
            id: 'EMPTY_REFERENCE_LIBRARY',
          })}
          testId="empty-reference-library"
          description={intl.formatMessage({
            id: 'EMPTY_REFERENCE_LIBRARY_DESCRIPTION',
          })}
        />
      </div>
    );
  };

  const columns = useMemo<TableColumn<Citation>[]>(() => {
    const columns: TableColumn<Citation>[] = [
      {
        id: 'author',
        accessorFn: (citation) => {
          const author = getAuthor(citation);
          return author === 'No authors' ? null : author;
        },
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'editor.modals.citations.fields.author' }),
        cell: (info) => {
          const citation = info.row.original;
          const author = getAuthor(citation);

          return (
            <Cell testId={`${citation.id}-author-column`}>
              {author === 'No authors' ? <Cell.Empty>{author}</Cell.Empty> : author}
            </Cell>
          );
        },
      },
      {
        id: 'title',
        accessorFn: (citation) => citation.title,
        enableSorting: false,
        size: 272,
        header: intl.formatMessage({ id: 'editor.modals.citations.fields.title' }),
        cell: (info) => {
          const citation = info.row.original;
          return (
            <Cell testId={`${citation.id}-title-column`}>
              {entryHasField(citation, 'url') ? (
                <div className={styles.linkCell}>
                  <Tooltip
                    content={intl.formatMessage({ id: 'SEE_FULL_TEXT' })}
                    placement="top"
                    testId={`${citation.id}-see-full-text`}
                  >
                    <Link
                      size="m"
                      variant="primary"
                      href={citation.url}
                      testId={`${citation.id}-see-full-text`}
                    >
                      {citation.title || citation.url}
                    </Link>
                  </Tooltip>
                </div>
              ) : (
                (entryHasField(citation, 'title') && <Cell.Empty>{citation.title}</Cell.Empty>) || (
                  <Cell.Empty>{intl.formatMessage({ id: 'global.noTitle' })}</Cell.Empty>
                )
              )}
            </Cell>
          );
        },
      },
      {
        id: 'published_in',
        accessorFn: (citation) => citation.source_title || citation.publisher,
        enableSorting: false,
        size: 144,
        header: intl.formatMessage({ id: 'global.publishedIn' }),
        cell: (info) => {
          const citation = info.row.original;

          return (
            <Cell testId={`${citation.id}-published-in-column`}>
              <>
                {(entryHasField(citation, 'source_title') && citation.source_title) ||
                  (entryHasField(citation, 'publisher') && citation.publisher)}
              </>
            </Cell>
          );
        },
      },
      {
        id: 'year',
        accessorFn: (citation) => citation.year,
        enableSorting: false,
        size: 64,
        header: intl.formatMessage({ id: 'global.year' }),
        cell: (info) => {
          const citation = info.row.original;

          return (
            <Cell testId={`${citation.id}-year-column`}>
              {entryHasField(citation, 'year') && citation.year}
            </Cell>
          );
        },
      },
    ];

    switch (selectedTab) {
      case 'documentLibrary': {
        if (!libraryExportMode) {
          columns.unshift({
            id: 'in_document',
            enableSorting: false,
            header: '',
            accessorFn: (citation) => insertedCitations?.includes(citation.id),
            size: 50,
            cell: (info) => {
              const citation = info.row.original;
              return (
                <Cell testId={`${citation.id}-in-document-column`}>
                  {insertedCitations && insertedCitations.includes(citation.id) && (
                    <Tooltip
                      content={intl.formatMessage({
                        id: 'editor.modals.citations.referenceCited',
                      })}
                      placement="top"
                      testId={`${citation.id}-in-document`}
                    >
                      <span>
                        <Icon
                          icon="quotes"
                          size="4"
                          testId={`${citation.id}-in-document`}
                          color="neutral"
                        />
                      </span>
                    </Tooltip>
                  )}
                </Cell>
              );
            },
          });
        }
        columns.push({
          id: 'added',
          accessorFn: (citation) => citation.time.creation,
          enableSorting: false,
          header: intl.formatMessage({ id: 'editor.modals.citations.fields.added' }),
          size: 100,
          cell: (info) => {
            const citation = info.row.original;
            return (
              <Cell testId={`${citation?.id}-added-column`}>
                {dayjs(citation?.time?.creation).format('MMM DD')}
              </Cell>
            );
          },
        });
        break;
      }
      default: {
        return columns;
      }
    }

    return columns;
  }, [libraryExportMode, selectedTab]);

  const rowsData = useMemo(() => {
    const list =
      selectedTab === 'documentLibrary'
        ? citations.list?.sort((a, b) =>
            dayjs(citations.dict[b].time?.creation).isAfter(citations.dict[a].time?.creation)
              ? 1
              : -1,
          )
        : citations.list;
    return list.map<TableRow<Citation>>((id) => {
      return {
        ...citations.dict[id],
      };
    });
  }, [citations]);

  const entryHasField = (citation: Citation | undefined, field: Citation.Field) => {
    const entry = citation?.entry;
    if (
      entry &&
      entry in ENTRIES &&
      ENTRIES[entry as keyof typeof ENTRIES].some((supportedField) => supportedField === field)
    ) {
      return !!citation[field];
    }

    return false;
  };

  const handleRowClick: onRowClickHandler<Citation> = (row) => {
    const { id } = row.original;
    if (!libraryExportMode) {
      dispatch(setSelected({ [id]: true }));
    }
    if (selectedTab === 'searchReferences') {
      const id = Number(row.id) + 1;
      //@ts-expect-error
      if (citations.dict[id]?.scopus_id) {
        dispatch(
          getScopusCitationAuthors({
            documentId,
            citationId: `${id} `,
            //@ts-expect-error
            scopusId: citations.dict[id].scopus_id,
          }),
        );
      }
      setRowSelected(Number(row.id));
      dispatch(setSelected({ [id]: true }));
    }
  };

  return (
    <div className={styles.root}>
      <Table
        isLoading={loading ?? false}
        identity="references"
        options={{ columns, data: rowsData }}
        renderFooter={() => null}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="references"
        renderEmptyState={renderEmptyView}
        selectable={
          (selectedTab === 'documentLibrary' && !libraryExportMode) ||
          selectedTab === 'searchReferences'
            ? false
            : true
        }
        onRowClick={handleRowClick}
        selection={rowSelected !== undefined ? { [rowSelected]: true } : selected}
      />
    </div>
  );
};

export default CitationsList;
